import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  IconPhoto,
  IconVideo,
  IconBrandYoutube,
  IconSparkles, // For Skills
} from '@tabler/icons-react';
import { cn } from '@/admin/lib/utils';

export interface SlashCommandMenuProps {
  open: boolean;
  top: number; // Viewport-relative Y coordinate from coordsAtPos
  left: number; // Viewport-relative X coordinate from coordsAtPos
  filter: string;
  onSelect: (item: SlashCommandItem) => void;
  onClose: () => void;
  containerRef?: React.RefObject<HTMLDivElement>; // Editor container for relative positioning
}

export interface SlashCommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  type: 'basic' | 'media' | 'embed' | 'component';
}

const ITEMS: SlashCommandItem[] = [
  {
    id: 'image',
    label: 'Image',
    description: 'Upload or embed with a link',
    icon: <IconPhoto className="w-4 h-4" />,
    type: 'media',
  },
  {
    id: 'video',
    label: 'Video',
    description: 'Upload or embed with a link',
    icon: <IconVideo className="w-4 h-4" />,
    type: 'media',
  },
  {
    id: 'youtube',
    label: 'YouTube',
    description: 'Embed a YouTube video',
    icon: <IconBrandYoutube className="w-4 h-4" />,
    type: 'embed',
  },
  {
    id: 'skill',
    label: 'Skill Card',
    description: 'Embed a Skill card',
    icon: <IconSparkles className="w-4 h-4 text-primary" />,
    type: 'component',
  },
];

export const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({
  open,
  top,
  left,
  filter,
  onSelect,
  onClose,
  containerRef,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  const filteredItems = ITEMS.filter(
    item =>
      item.label.toLowerCase().includes(filter.toLowerCase()) ||
      item.description?.toLowerCase().includes(filter.toLowerCase())
  );

  // Calculate position relative to the container
  const position = useMemo(() => {
    if (!containerRef?.current) {
      // Fallback to viewport-relative (fixed positioning)
      return { top, left, useFixed: true };
    }
    const containerRect = containerRef.current.getBoundingClientRect();
    return {
      top: top - containerRect.top,
      left: left - containerRect.left,
      useFixed: false,
    };
  }, [top, left, containerRef]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filter]);

  // Handle keyboard navigation - use capture phase to intercept before CodeMirror
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(prev =>
            prev < filteredItems.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (filteredItems[selectedIndex]) {
            onSelect(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
      }
    };

    // Use capture phase to intercept events before they reach CodeMirror
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, filteredItems, selectedIndex, onSelect, onClose]);

  // Handle outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  // Scroll active item into view
  useEffect(() => {
    const activeItem = menuRef.current?.querySelector('[data-active="true"]');
    if (activeItem) {
      activeItem.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (!open) return null;

  const menuContent = (
    <div
      ref={menuRef}
      className={cn(
        'z-50 w-72 bg-popover rounded-md border shadow-md overflow-hidden animate-in fade-in zoom-in-95 duration-100',
        position.useFixed ? 'fixed' : 'absolute'
      )}
      style={{ top: position.top, left: position.left }}
    >
      <div className="max-h-[300px] overflow-y-auto p-1">
        {filteredItems.length === 0 ? (
          <div className="p-3 text-sm text-muted-foreground text-center">
            No results
          </div>
        ) : (
          <div className="space-y-1">
            {filteredItems.map((item, index) => (
              <button
                key={item.id}
                data-active={index === selectedIndex}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm text-left outline-none transition-colors',
                  index === selectedIndex
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-muted text-foreground'
                )}
                onClick={() => onSelect(item)}
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-background border border-muted shadow-sm shrink-0">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{item.label}</div>
                  {item.description && (
                    <div className="text-xs text-muted-foreground truncate">
                      {item.description}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render inside container if available, otherwise use portal (fallback)
  if (containerRef?.current && !position.useFixed) {
    return menuContent;
  }

  return createPortal(menuContent, document.body);
};
